<template>
  <div>
    <div>
      <div v-if="loading" v-loading="loading || proReportDataLoading" element-loading-spinner="atom-audit-loader" style="min-height: 200px;"></div>

      <div v-else>
        <div v-for="(item, index) in allData" :key="index" :class="{ 'provider-section': index > 0 }">
          <div style="position: relative; float: right; margin: 0px 0px -10px 0px;">
            <el-dropdown trigger="click" @command="handleTableCommands">
              <span class="el-dropdown-link">
                <i title="table actions" class="mdi mdi-dots-horizontal" style="font-size: 20px; cursor: pointer;"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="buildTableCommands('download', item)">Download</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <h1 class="provider-name">{{ item.providerName }}</h1>
          <el-table :id="`proRecordSummary-${item.providerName}`" class="proRecordSummaryTable" :ref="`proRecordSummary-${item.providerName}`" :data="item.records" empty-text="No Rebill Data Available" max-height="425" stripe :cell-class-name="cellClasses">
            <el-table-column prop="patientNumber" label="Pat. Num." sortable>
              <template slot-scope="scope">
                <a target="_blank" :href="`/reviews/pro/${scope.row.proRecordId}`">{{ scope.row.patientNumber }}</a>
              </template>
            </el-table-column>
            <el-table-column prop="payorName" label="Payor" sortable> </el-table-column>
            <el-table-column prop="dateOfService" label="Date of Serv." sortable>
              <template slot-scope="scope">
                {{ scope.row.dateOfService ? format(parseISO(scope.row.dateOfService), 'M/d/yyyy') : '' }}
              </template>
            </el-table-column>
            <el-table-column label="Dx Coding" :width="dxColumnWidth" sortable>
              <template slot-scope="scope">
                <template v-if="scope.row.isLevel2">
                  <div class="coding-container">
                    <div class="code-column" v-if="showPreDxCodeType(scope.row)">
                      <div class="codeTypeHeader" :title="scope.row.dxs.length > 0 ? scope.row.dxs[0].preDxCodeDesc : ''">
                        {{ scope.row.dxs.length > 0 ? scope.row.dxs[0].preDxCodeDesc : '' }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(dx, index) in scope.row.dxs" :key="index" :class="{ 'red-font': dx.preDxCodeDeleted, bold: isDxRowChange(dx) }">
                          {{ dx.preDx ? dx.preDx : '' }}
                        </li>
                      </ol>
                    </div>

                    <div class="code-column" v-if="showOriginalDxCodeType(scope.row)">
                      <div class="codeTypeHeader" :title="scope.row.dxs.length > 0 ? scope.row.dxs[0].codeDesc : ''">
                        {{ scope.row.dxs.length > 0 ? scope.row.dxs[0].codeDesc : '' }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(dx, index) in scope.row.dxs" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                          {{ dx.preDxDeleted ? 'DEL' : dx.originalDx ? dx.originalDx : '' }}
                        </li>
                      </ol>
                    </div>

                    <div class="code-column">
                      <div class="codeTypeHeader" title="Recommendations">
                        Recommendations
                      </div>
                      <ol class="code-list">
                        <li v-for="(dx, index) in scope.row.dxs" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                          {{ dx.originalDeleted ? 'DEL' : dx.recommendedDx ? dx.recommendedDx : '' }}
                        </li>
                      </ol>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div v-for="codeType in getCodeTypes(scope.row.dxs)" :key="codeType" class="coding-container">
                    <div class="code-column">
                      <div class="codeTypeHeader" :title="codeType">
                        {{ codeType }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(dx, index) in scope.row.dxs.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                          {{ dx.originalDx ? dx.originalDx : '' }}
                        </li>
                      </ol>
                    </div>
                    <div class="code-column">
                      <div class="codeTypeHeader" title="Recommendations">
                        Recommendations
                      </div>
                      <ol class="code-list">
                        <li v-for="(dx, index) in scope.row.dxs.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                          {{ dx.originalDeleted ? 'DEL' : dx.recommendedDx ? dx.recommendedDx : '' }}
                        </li>
                      </ol>
                    </div>
                  </div>
                </template>
              </template>
            </el-table-column>
            <el-table-column label="CPT Coding" :width="cptColumnWidth" sortable>
              <template slot-scope="scope">
                <template v-if="scope.row.isCptLevel2">
                  <div class="coding-container">
                    <div class="code-column" v-if="showPreCptCodeType(scope.row)">
                      <div class="codeTypeHeader" :title="scope.row.cpts.length > 0 ? scope.row.cpts[0].preCptCodeDesc : ''">
                        {{ scope.row.cpts.length > 0 ? scope.row.cpts[0].preCptCodeDesc : '' }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(cpt, index) in scope.row.cpts" :key="index" :class="{ 'red-font': cpt.preCptDeleted, bold: isCptRowChange(cpt) }">
                          {{ cpt.preCpt ? cpt.preCpt : '' }}
                        </li>
                      </ol>
                    </div>

                    <div class="code-column" v-if="showOriginalCptCodeType(scope.row)">
                      <div class="codeTypeHeader" :title="scope.row.cpts.length > 0 ? scope.row.cpts[0].codeDesc : ''">
                        {{ scope.row.cpts.length > 0 ? scope.row.cpts[0].codeDesc : '' }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(cpt, index) in scope.row.cpts" :key="index" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }">
                          {{ cpt.preCptDeleted ? 'DEL' : getOriginalCpt(cpt) }}
                        </li>
                      </ol>
                    </div>

                    <div class="code-column">
                      <div class="codeTypeHeader" title="Recommendations">
                        Recommendations
                      </div>
                      <ol class="code-list">
                        <li v-for="(cpt, index) in scope.row.cpts" :key="index" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }" v-html="getRecommendedCpt(cpt)"></li>
                      </ol>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div v-for="codeType in getCodeTypes(scope.row.cpts)" :key="codeType" class="coding-container">
                    <div class="code-column">
                      <div class="codeTypeHeader">
                        {{ codeType }}
                      </div>
                      <ol class="code-list">
                        <li v-for="(cpt, index) in scope.row.cpts.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }">
                          {{ getOriginalCpt(cpt) }}
                        </li>
                      </ol>
                    </div>
                    <div class="cpt-column">
                      <div class="codeTypeHeader">
                        Recommendations
                      </div>
                      <ol class="code-list">
                        <li v-for="(cpt, index) in scope.row.cpts.filter(x => x.codeDesc === codeType)" :key="index" v-html="getRecommendedCpt(cpt)" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }"></li>
                      </ol>
                    </div>
                  </div>
                </template>
              </template>
            </el-table-column>
            <el-table-column prop="reasonForChange" label="Reason for Change" sortable> </el-table-column>
            <el-table-column prop="keyFindings" :label="tenantSetup.enableProKeyFindings ? 'Key Findings' : 'Action Taken'" sortable width="350">
              <template slot-scope="scope">
                <div style="white-space: pre-wrap; line-height: 1.5;">{{ getKeyFindings(scope.row) }}</div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <el-dialog :visible.sync="dialogVisible" center destroy-on-close width="75%">
      <accountList v-if="dialogVisible" :header="detailsHeader" :proRecordIds="subProRecordIds"></accountList>
    </el-dialog>

    <el-dialog :visible.sync="summaryDialogVisible" center destroy-on-close width="75%">
      <PROSummary v-if="summaryDialogVisible" :item="summaryRow"></PROSummary>
    </el-dialog>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import axios from '@/utilities/axios-global'
  import hiaFilters from '@/mixins/hiaFilters'
  import money from '@/mixins/money'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import accountList from './accoutList'
  import PROSummary from './PRO_Summary'
  import { format, parseISO } from 'date-fns'

  export default {
    name: 'PROByCoder',
    mixins: [hiaFilters, money, dashboardHelper],
    props: ['proRecordIds', 'dashboards', 'hideDetails', 'codeType', 'codeTypes'],
    components: {
      accountList,
      PROSummary
    },
    data: function() {
      return {
        format: format,
        parseISO: parseISO,
        allData: [],
        filterCriteria: '',
        loading: false,
        dialogVisible: false,
        summaryDialogVisible: false,
        subProRecordIds: null,
        summaryRow: {},
        detailsHeader: '',
        hasWideDxRecords: false,
        hasWideCptRecords: false
      }
    },
    methods: {
      ...mapActions('managedLists/tenantSetup/', ['GET_TENANT_SETUP']),
      async getData() {
        const payload = {
          ids: this.proRecordIds,
          codeTypes: this.codeTypes
        }
        try {
          this.loading = true
          const response = await axios.post(`${this.baseApiUrl}/reviews/pro/worksheets?changesOnly=true`, payload)
          const uniqueProviders = [...new Set(response.data.map(item => item.physicianName))]
          uniqueProviders.sort((a, b) => (a > b ? 1 : -1))
          const allData = []
          uniqueProviders.forEach(provider => {
            const providerData = response.data.filter(item => item.physicianName === provider)
            allData.push({
              providerName: provider,
              records: providerData
            })
          })
          this.allData = allData

          // Pre-calculate if any records need wide Dx columns
          this.hasWideDxRecords = allData.some(provider => provider.records.some(record => record.dxs.some(dx => this.showPreDxCodeType(record) && this.showOriginalDxCodeType(record))))
          this.hasWideCptRecords = allData.some(provider => provider.records.some(record => record.cpts.some(cpt => this.showPreCptCodeType(record) && this.showOriginalCptCodeType(record))))
        } catch (err) {
          this.allData = []
          this.hasWideDxRecords = false
          this.hasWideCptRecords = false
        } finally {
          this.loading = false
        }
      },
      showPreCptCodeType(row) {
        if (row.cpts.length > 0 && this.codeTypes.length > 0) {
          return this.codeTypes.includes(row.cpts[0].preCptCodeType)
        }
        return true
      },
      showOriginalCptCodeType(row) {
        if (row.cpts.length > 0 && this.codeTypes.length > 0) {
          return this.codeTypes.includes(row.cpts[0].codeType)
        }
        return true
      },
      getKeyFindings(row) {
        if (this.tenantSetup.enableProKeyFindings) {
          // Convert HTML to plain text, preserving line breaks
          return this.htmlToPlainText(row.keyFindings)
        }
        // For findings array, join each findingDetails as plain text
        return row.findings.map(x => this.htmlToPlainText(x.findingDetails)).join('\n\n')
      },
      htmlToPlainText(html) {
        if (!html) return ''
        // Replace <br> and <br/> with newlines
        let text = html.replace(/<br\s*\/?>/gi, '\n')
        // Remove all other HTML tags
        text = text.replace(/<[^>]+>/g, '')
        // decode HTML entities
        const txt = document.createElement('textarea')
        txt.innerHTML = text
        let result = txt.value
        // Remove trailing newlines (but not those in the middle)
        result = result.replace(/[\n\r]+$/g, '')
        return result
      },
      getCodeTypes(arr) {
        const codeTypes = [...new Set(arr.map(x => x.codeDesc))]
        return codeTypes.sort((a, b) => (a > b ? 1 : -1))
      },
      getOriginalCpt(cpt) {
        if (!cpt.originalCPT) return ''
        const modifier = cpt.originalModifierCalc || ''
        return cpt.originalCPT + modifier
      },
      getRecommendedCpt(cpt) {
        if (cpt.originalDeleted) {
          return 'DEL'
        } else {
          return cpt.calcRecCPT
        }
      },
      showPreDxCodeType(row) {
        if (row.dxs.length > 0 && this.codeTypes.length > 0) {
          return this.codeTypes.includes(row.dxs[0].preDxCodeType)
        }
        return true
      },
      showOriginalDxCodeType(row) {
        if (row.dxs.length > 0 && this.codeTypes.length > 0) {
          return this.codeTypes.includes(row.dxs[0].codeType)
        }
        return true
      },
      isDxRowChange(dx) {
        if (dx.recommendedDx || dx.originalDeleted) {
          return true
        }

        if (((this.codeTypes.length > 0 && this.codeTypes.includes(dx.preDxCodeType)) || this.codeTypes.length === 0) && (dx.preDxDeleted || dx.originalDx)) {
          return true
        }

        return false
      },
      isCptRowChange(cpt) {
        if (cpt.recommendedCpt || cpt.originalDeleted) {
          return true
        }
        return false
      },
      updateAcctList(row) {
        this.detailsHeader = row.cliName
        this.subProRecordIds = row.accountNumbers
        this.dialogVisible = !this.dialogVisible
      },
      showSummary(row) {
        this.summaryRow = row
        this.summaryDialogVisible = !this.summaryDialogVisible
      },
      getSummaries(param) {
        return this.getSummary(param)
      },
      cellClasses({ row, column, rowIndex, columnIndex }) {
        if (column.property === 'cliName' || column.property === 'codeDesc') {
          return 'textAlignLeft'
        }
        if (column.property === 'details') {
          return 'textAlignCenter'
        }
      },
      getTextDxs(dxs, codeProperty) {
        if (!dxs || !dxs.length) return ''
        const codeTypes = this.getCodeTypes(dxs)
        return codeTypes
          .map(codeType => {
            const codes = dxs
              .filter(x => x.codeDesc === codeType)
              .map((x, index) => {
                if (codeProperty === 'originalDx') {
                  return `${index + 1}. ${x[codeProperty] || ''}`
                }
                return `${index + 1}. ${x[codeProperty] || ''}`
              })
              .join('\n')
            return `${codeType}\n${codes}`
          })
          .join('\n\n')
      },
      getTextCpts(cpts, codeProperty) {
        if (!cpts || !cpts.length) return ''
        const codeTypes = this.getCodeTypes(cpts)
        return codeTypes
          .map(codeType => {
            const codes = cpts
              .filter(x => x.codeDesc === codeType)
              .map((x, index) => {
                if (codeProperty === 'originalCPT') {
                  return `${index + 1}. ${this.getOriginalCpt(x)}`
                }
                return `${index + 1}. ${this.getRecommendedCpt(x)
                  .replace('<span class="delCode">', '')
                  .replace('</span>', '')}`
              })
              .join('\n')
            return `${codeType}\n${codes}`
          })
          .join('\n\n')
      },
      handleCommand(command) {
        this.$emit('showModal', command)
      },
      buildCommand(outerDashboardRow, selectedDashboard, key) {
        const command = {
          proRecordIds: outerDashboardRow.accountNumbers,
          component: selectedDashboard.component,
          title: outerDashboardRow.coder + ' - ' + selectedDashboard.name,
          codeType: key
        }
        return command
      },
      handleTableCommands(command) {
        const data = command.key

        let csvString = `${data.providerName}\n\n`

        csvString += 'Patient Number,Payor,Date of Service,Dx Coding,CPT Coding,Reason for Change,' + (this.tenantSetup.enableProKeyFindings ? 'Key Findings' : 'Action Taken') + '\n'

        data.records.forEach(record => {
          const dateOfService = record.dateOfService ? this.format(parseISO(record.dateOfService), 'M/d/yyyy') : ''
          const dxCoding = this.getTextDxs(record.dxs, 'originalDx') + ' | ' + this.getTextDxs(record.dxs, 'recommendedDx')
          const cptCoding = this.getTextCpts(record.cpts, 'originalCPT') + ' | ' + this.getTextCpts(record.cpts, 'recommendedCpt')

          csvString += [this.escapeCsvField(record.patientNumber), this.escapeCsvField(record.payorName), this.escapeCsvField(dateOfService), this.escapeCsvField(dxCoding), this.escapeCsvField(cptCoding), this.escapeCsvField(record.reasonForChange), this.escapeCsvField(this.getKeyFindings(record))].join(',') + '\n'
        })

        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvString], {
          type: 'text/csv;charset=utf-8'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `checklist_summary_${data.providerName}.csv`
        link.click()
      },
      buildTableCommands(command, key) {
        const commandObject = {
          command: command,
          key: key
        }
        return commandObject
      },
      escapeCsvField(value) {
        if (value == null) return ''
        const str = String(value)
        if (str.includes('"')) {
          // Escape double quotes
          return `"${str.replace(/"/g, '""')}"`
        }
        if (str.includes(',') || str.includes('\n') || str.includes('\r')) {
          // Wrap in quotes if contains comma or newline
          return `"${str}"`
        }
        return str
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('reports/', ['proReportDataLoading']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup']),
      dxColumnWidth() {
        return this.hasWideDxRecords ? 380 : 300
      },
      cptColumnWidth() {
        return this.hasWideCptRecords ? 380 : 300
      }
    },
    mounted: async function() {
      await this.GET_TENANT_SETUP()
      await this.getData()
    },
    activated: async function() {
      await this.getData()
    },
    watch: {
      proRecordIds: async function() {
        await this.getData()
      },
      codeTypes: async function() {
        await this.getData()
      }
    }
  }
</script>

<style scoped>
  /* .provider-name {
    font-weight: 600;
  } */

  .provider-section:not(:first-child) {
    margin-top: 50px;
  }

  .line-through {
    text-decoration: line-through;
  }

  .code-list {
    margin: 0;
    padding-left: 0px;
    list-style-position: inside;
    margin: 4px 0px 9px 0px;
  }

  .code-list li {
    padding-left: 0;
  }

  >>> .delCode {
    color: red;
    text-decoration: line-through;
  }

  .red-font {
    color: red;
  }

  .bold {
    font-weight: bold;
  }

  .codeTypeHeader {
    display: block;
    text-align: left;
    font-style: italic;
    line-height: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    min-width: 0;
  }

  .codeTypeSquare {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #3378aa7c;
    border-radius: 2px;
    margin-right: 4px;
    margin-bottom: 2px;
    vertical-align: middle;
  }

  >>> .proRecordSummaryTable td {
    vertical-align: top;
  }

  .coding-container {
    display: flex;
    justify-content: flex-start;
    gap: 20px;
  }

  .code-column {
    flex: 0 0 auto;
    min-width: 0;
  }
</style>
